import type {
  Effect,
  EvalCtx,
  HoldingTickInput,
  HoldingTickOutput,
  RoundEndOutput,
  RoundStartOutput,
  TradeCloseInput,
  TradeCloseOutput,
} from "./types";

// Pure ops: math only, deterministic

export function applyPhaseCosts(
  input: TradeCloseInput,
  effects: ReadonlyArray<Effect>,
  ctx: EvalCtx
): { fees: { buy?: number; sell?: number } } {
  let fees = { ...input.currentFees };
  for (const e of effects) {
    if (e.op === "trade_fee_pct") {
      const buy = (fees.buy ?? 0) + (e.buy ?? 0);
      const sell = (fees.sell ?? 0) + (e.sell ?? 0);
      fees = { buy, sell };
    }
  }
  if (ctx.runtime && ctx.runtime.opTotals) {
    // track totals optionally
  }
  return { fees };
}

export function applyPhaseLossGuards(
  baseLossPct: number | undefined,
  effects: ReadonlyArray<Effect>
): { lossPct?: number } {
  if (baseLossPct == null) return {};
  let loss = baseLossPct;
  for (const e of effects) {
    if (e.op === "loss_nullify") loss = 0;
    else if (e.op === "loss_reduction_pct")
      loss = Math.max(0, loss * (1 - e.pct));
  }
  return { lossPct: loss };
}

export function applyPhaseYields(
  holdMs: number,
  effects: ReadonlyArray<Effect>
): { addProfitPct: number } {
  let addPct = 0;
  for (const e of effects) {
    if (e.op === "profit_add_per_sec") {
      const seconds = Math.max(0, holdMs) / 1000;
      addPct += e.pct * seconds * 100; // convert to percent
    }
  }
  return { addProfitPct: addPct };
}

export function applyPhaseProfitScalers(
  baseProfitPct: number | undefined,
  addProfitPct: number,
  effects: ReadonlyArray<Effect>
): { profitPct?: number } {
  if (baseProfitPct == null) return {};
  let profit = baseProfitPct + addProfitPct;
  for (const e of effects) {
    if (e.op === "profit_mult") {
      profit *= e.value;
    }
  }
  return { profitPct: profit };
}

export function applyRoundAccountOpsOnStart(
  effects: ReadonlyArray<Effect>
): RoundStartOutput {
  let addTimeSec = 0;
  let addBuys = 0;
  let addLives = 0;
  let leverageMult = 1;
  let autoLiquidateDrawdownPct: number | undefined;
  for (const e of effects) {
    switch (e.op) {
      case "round_time_add_sec":
        addTimeSec += e.value;
        break;
      case "round_buys_add":
        addBuys += e.value;
        break;
      case "grant_life":
        addLives += e.value;
        break;
      case "leverage_mult":
        leverageMult *= e.value;
        break;
      case "auto_liquidate_at_drawdown_pct":
        autoLiquidateDrawdownPct = Math.min(
          autoLiquidateDrawdownPct ?? e.pct,
          e.pct
        );
        break;
      default:
        break;
    }
  }
  return {
    addTimeSec,
    addBuys,
    addLives,
    leverageMult,
    autoLiquidateDrawdownPct,
  };
}

export function applyRoundEndOps(
  effects: ReadonlyArray<Effect>
): RoundEndOutput {
  let bankMult = 1;
  for (const e of effects) {
    if (e.op === "bank_mult") bankMult *= e.value;
  }
  return { bankMult };
}

export function applyHoldingTick(
  input: HoldingTickInput,
  effects: ReadonlyArray<Effect>
): HoldingTickOutput {
  const { addProfitPct } = applyPhaseYields(input.holdMs, effects);
  return { addedProfitPct: addProfitPct };
}

export function reduceTradeClose(
  input: TradeCloseInput,
  effects: ReadonlyArray<Effect>,
  ctx: EvalCtx
): TradeCloseOutput {
  // Phase 1: costs
  const p1 = applyPhaseCosts(input, effects, ctx);
  // Phase 2: loss guards
  const p2 = applyPhaseLossGuards(input.baseLossPct, effects);
  // Phase 3: yields
  const p3 = applyPhaseYields(input.holdMs, effects);
  // Phase 4: profit scalers
  const p4 = applyPhaseProfitScalers(
    input.baseProfitPct,
    p3.addProfitPct,
    effects
  );
  return { fees: p1.fees, ...p2, ...p4 };
}
